import { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux/store/hooks";
import { navigate } from "../../../redux/slice/layoutSlice";
import { clearSelected } from "../../../redux/slice/selectionSlice";
import {
  useGetPlanQuery,
  useCreatePlanMutation,
  useUpdatePlanMutation,
} from "../../../redux/api";
import type { Plan as PlanModel } from "../../../redux/api";
import { Field } from "../../../components/Field";

const ENTITY = "plan";
const LIST_PATH = "/plans";

type PlanForm = {
  code: string;
  name: string;
  description: string;
  monthlyPrice: string;
  yearlyPrice: string;
  currency: string;
  usdMonthlyPrice: string;
  usdYearlyPrice: string;
  isActive: string;
  users: string;
  products: string;
  storageGB: string;
  apiCallsPerMonth: string;
};

const emptyForm: PlanForm = {
  code: "PRO",
  name: "",
  description: "",
  monthlyPrice: "0",
  yearlyPrice: "0",
  currency: "INR",
  usdMonthlyPrice: "0",
  usdYearlyPrice: "0",
  isActive: "yes",
  users: "0",
  products: "0",
  storageGB: "0",
  apiCallsPerMonth: "0",
};

const n = (v: string) => Number(v) || 0;

const formFromPlan = (existing?: PlanModel): PlanForm => {
  if (!existing) return emptyForm;
  const l = existing.limits ?? {};
  return {
    code: existing.code ?? "PRO",
    name: existing.name ?? "",
    description: existing.description ?? "",
    monthlyPrice: String(existing.monthlyPrice ?? 0),
    yearlyPrice: String(existing.yearlyPrice ?? 0),
    currency: existing.currency ?? "INR",
    usdMonthlyPrice: String(existing.usdMonthlyPrice ?? 0),
    usdYearlyPrice: String(existing.usdYearlyPrice ?? 0),
    isActive: existing.isActive === false ? "no" : "yes",
    users: String(l.users ?? 0),
    products: String(l.products ?? 0),
    storageGB: String(l.storageGB ?? 0),
    apiCallsPerMonth: String(l.apiCallsPerMonth ?? 0),
  };
};

const Plan = () => {
  const dispatch = useAppDispatch();
  const selectedId = useAppSelector((s) => s.selection[ENTITY]);
  const isEdit = !!selectedId;

  const { data: existing } = useGetPlanQuery(selectedId ?? "", {
    skip: !selectedId,
  });

  const [createPlan, { isLoading: creating }] = useCreatePlanMutation();
  const [updatePlan, { isLoading: updating }] = useUpdatePlanMutation();

  const [form, setForm] = useState<PlanForm>(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | undefined>>({});

  const saving = creating || updating;

  useEffect(() => {
    if (existing) setForm(formFromPlan(existing));
    setFieldErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing?.id, existing?.updatedAt]);

  const setField = (key: keyof PlanForm, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const baseline = useMemo(() => formFromPlan(existing), [existing]);
  const dirty = JSON.stringify(form) !== JSON.stringify(baseline);

  const goBack = () => {
    dispatch(clearSelected(ENTITY));
    dispatch(navigate(LIST_PATH));
  };

  const handleSave = async () => {
    setFormError(null);

    if (!form.name.trim()) {
      setFieldErrors({ name: "Plan name is required." });
      return;
    }
    setFieldErrors({});

    const payload = {
      code: form.code,
      name: form.name.trim(),
      description: form.description.trim() || undefined,
      monthlyPrice: n(form.monthlyPrice),
      yearlyPrice: n(form.yearlyPrice),
      currency: form.currency,
      usdMonthlyPrice: n(form.usdMonthlyPrice),
      usdYearlyPrice: n(form.usdYearlyPrice),
      isActive: form.isActive !== "no",
      limits: {
        users: n(form.users),
        products: n(form.products),
        storageGB: n(form.storageGB),
        apiCallsPerMonth: n(form.apiCallsPerMonth),
      },
    };

    try {
      if (isEdit && selectedId) {
        await updatePlan({ id: selectedId, input: payload }).unwrap();
      } else {
        await createPlan(payload).unwrap();
      }
      goBack();
    } catch (err) {
      setFormError(
        (err as { message?: string })?.message ?? "Failed to save plan.",
      );
    }
  };

  return (
    <div className="dashboard">
      <div className="dashboard__head">
        <div>
          <h1>{isEdit ? "Edit plan" : "New plan"}</h1>
          <p>Subscription plan and its limits.</p>
        </div>
        <button type="button" className="btn" onClick={goBack}>
          ← Back to plans
        </button>
      </div>

      <section className="panel">
        {formError ? <div className="form-error">{formError}</div> : null}

        <div className="form-grid">
          <label className="field">
            Code
            <select
              value={form.code}
              onChange={(e) => setField("code", e.target.value)}
            >
              <option value="PRO">PRO</option>
              <option value="PRO_PLUS">PRO_PLUS</option>
              <option value="CUSTOM">CUSTOM</option>
            </select>
          </label>
          <Field label="Name *" error={fieldErrors.name}>
            <input
              value={form.name}
              onChange={(e) => {
                setField("name", e.target.value);
                setFieldErrors((prev) => ({ ...prev, name: undefined }));
              }}
            />
          </Field>
          <label className="field">
            Monthly price
            <input
              type="number"
              value={form.monthlyPrice}
              onChange={(e) => setField("monthlyPrice", e.target.value)}
            />
          </label>
          <label className="field">
            Yearly price
            <input
              type="number"
              value={form.yearlyPrice}
              onChange={(e) => setField("yearlyPrice", e.target.value)}
            />
          </label>
          <label className="field">
            Currency
            <select
              value={form.currency}
              onChange={(e) => setField("currency", e.target.value)}
            >
              <option value="INR">INR</option>
              <option value="USD">USD</option>
            </select>
          </label>
          <label className="field">
            USD monthly price (international)
            <input
              type="number"
              value={form.usdMonthlyPrice}
              onChange={(e) => setField("usdMonthlyPrice", e.target.value)}
            />
            <span className="field__hint">
              Shown to visitors outside India — set independently, not an FX
              conversion of the price above.
            </span>
          </label>
          <label className="field">
            USD yearly price (international)
            <input
              type="number"
              value={form.usdYearlyPrice}
              onChange={(e) => setField("usdYearlyPrice", e.target.value)}
            />
          </label>
          <label className="field">
            Active
            <select
              value={form.isActive}
              onChange={(e) => setField("isActive", e.target.value)}
            >
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </label>
          <label className="field field--full">
            Description
            <textarea
              value={form.description}
              onChange={(e) => setField("description", e.target.value)}
            />
          </label>
        </div>

        <div className="panel__head" style={{ marginTop: "1.5rem" }}>
          <div>
            <h2>Limits</h2>
            <p className="panel__sub">Per-plan usage caps</p>
          </div>
        </div>

        <div className="form-grid">
          <label className="field">
            Users
            <input
              type="number"
              value={form.users}
              onChange={(e) => setField("users", e.target.value)}
            />
          </label>
          <label className="field">
            Products
            <input
              type="number"
              value={form.products}
              onChange={(e) => setField("products", e.target.value)}
            />
          </label>
          <label className="field">
            Storage (GB)
            <input
              type="number"
              value={form.storageGB}
              onChange={(e) => setField("storageGB", e.target.value)}
            />
            {form.code === "CUSTOM" ? (
              <span className="field__hint">
                Custom plans are unlimited/negotiated — limits aren't enforced.
              </span>
            ) : null}
          </label>
          <label className="field">
            API calls / month
            <input
              type="number"
              value={form.apiCallsPerMonth}
              onChange={(e) => setField("apiCallsPerMonth", e.target.value)}
            />
          </label>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="btn"
            onClick={goBack}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={handleSave}
            disabled={saving || !dirty}
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create plan"}
          </button>
        </div>
      </section>
    </div>
  );
};

export default Plan;
